import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Coins, Shield, ShoppingBag, Trophy, Landmark, Users, Sparkles, Copy,
  Loader2, Check, Lock,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const HOUSE_META = {
  lions:   { emoji: '🦁', color: 'from-red-600 to-red-900',      text: 'text-red-100' },
  dragons: { emoji: '🐉', color: 'from-emerald-600 to-emerald-900', text: 'text-emerald-100' },
  eagles:  { emoji: '🦅', color: 'from-blue-600 to-blue-900',    text: 'text-blue-100' },
  wolves:  { emoji: '🐺', color: 'from-purple-600 to-purple-900', text: 'text-purple-100' },
};

const SubTab = ({ active, onClick, children, testid, icon: Icon }) => (
  <button onClick={onClick} data-testid={testid}
    className={`h-9 px-4 rounded-full text-xs font-bold uppercase tracking-wider transition inline-flex items-center gap-1.5 ${
      active ? 'bg-[#D3D86F] text-black' : 'bg-white/10 text-white/70 hover:bg-white/20'
    }`}>
    {Icon && <Icon className="h-3.5 w-3.5" />}
    {children}
  </button>
);

const Empty = ({ children }) => (
  <div className="text-xs text-white/50 italic p-3">{children}</div>
);

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------
const OverviewView = ({ data, houseLeaderboard }) => {
  const p = data.profile || {};
  const w = data.wallet || {};
  const house = HOUSE_META[p.house] || HOUSE_META.lions;
  return (
    <div className="space-y-4" data-testid="winhouse-overview">
      <div className={`bg-gradient-to-br ${house.color} rounded-2xl p-5 border border-white/10`}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#D3D86F]">Your House</div>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-4xl">{house.emoji}</span>
              <div>
                <div className={`text-2xl font-black ${house.text}`} data-testid="wh-house-name">
                  House {p.house ? p.house[0].toUpperCase() + p.house.slice(1) : '—'}
                </div>
                <div className="text-white/60 text-xs">Assigned when you joined Winhouse</div>
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#D3D86F]">WinCoins</div>
            <div className="text-3xl font-black text-white mt-1" data-testid="wh-balance">
              {w.balance || 0}
            </div>
            <div className="text-white/50 text-[10px] mt-0.5">
              earned {w.lifetime_earned || 0} · spent {w.lifetime_spent || 0}
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-wider text-white/60 font-bold mb-2">House Leaderboard</div>
        <div className="grid grid-cols-2 gap-2" data-testid="wh-house-leaderboard">
          {(houseLeaderboard || []).map((h, i) => (
            <div key={h.key} className={`bg-white/5 border border-white/10 rounded-xl p-3 ${p.house === h.key ? 'ring-2 ring-[#D3D86F]' : ''}`}
              data-testid={`wh-house-row-${h.key}`}>
              <div className="flex items-center gap-2">
                <span className="text-xl">{h.emoji}</span>
                <div>
                  <div className="text-white font-bold text-sm">{h.name}</div>
                  <div className="text-white/50 text-[10px]">Rank #{i + 1} · {h.members} members</div>
                </div>
              </div>
              <div className="mt-2 text-[10px] text-white/60">
                <span className="text-[#D3D86F] font-bold">{h.points}</span> pts · {h.xp} XP · {h.coins} coins
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-wider text-white/60 font-bold mb-2">Recent Coin Activity</div>
        <div className="space-y-1.5" data-testid="wh-ledger">
          {(data.ledger || []).length === 0 && <Empty>No coin activity yet — complete a topic or challenge to earn WinCoins.</Empty>}
          {(data.ledger || []).slice(0, 8).map((l) => (
            <div key={l.ledger_id} className="flex items-center justify-between bg-white/5 border border-white/10 rounded-lg px-3 py-2"
              data-testid={`wh-ledger-${l.ledger_id}`}>
              <div>
                <div className="text-white text-xs font-bold">{l.kind.replace(/_/g, ' ')}</div>
                <div className="text-white/40 text-[10px]">{new Date(l.at).toLocaleString()}</div>
              </div>
              <div className={`text-sm font-black ${l.delta > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {l.delta > 0 ? '+' : ''}{l.delta}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Avatar Customiser
// ---------------------------------------------------------------------------
const CATEGORIES = ['hair', 'hoodie', 'glasses', 'hat', 'pet', 'frame', 'fx', 'theme'];

const AvatarView = ({ data, onSaved }) => {
  const [avatar, setAvatar] = useState(data.profile?.avatar || {});
  const [saving, setSaving] = useState(false);
  const owned = useMemo(
    () => new Set((data.purchases || []).map((p) => p.item_id)),
    [data.purchases]
  );
  const catalog = data.shop_catalog || [];

  const setSlot = (category, item_id) => {
    setAvatar({ ...avatar, [category]: item_id });
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await ax.post('/api/me/economy/avatar', { avatar });
      toast.success('Avatar saved');
      onSaved(r.data.avatar);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="winhouse-avatar">
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
        <div className="text-xs uppercase tracking-wider text-white/60 font-bold mb-3">Preview</div>
        <div className="flex items-center justify-center py-6 bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-white/10"
          data-testid="wh-avatar-preview">
          <div className="relative">
            <div className="w-28 h-28 rounded-full bg-[#D3D86F]/20 border-4 border-[#D3D86F] flex items-center justify-center text-5xl">
              {HOUSE_META[data.profile?.house]?.emoji || '👤'}
            </div>
            <div className="text-center mt-3 text-white text-xs">
              {CATEGORIES.filter(c => avatar[c]).map(c => (
                <span key={c} className="inline-block mx-1 px-2 py-0.5 bg-white/10 rounded text-[10px]">
                  {c}: {avatar[c]}
                </span>
              ))}
            </div>
          </div>
        </div>
        <button onClick={save} disabled={saving}
          className="mt-4 h-10 w-full rounded-xl bg-[#D3D86F] hover:bg-[#c4ca5e] text-black text-xs uppercase tracking-wider font-black disabled:opacity-50"
          data-testid="wh-avatar-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin inline" /> : 'Save Avatar'}
        </button>
      </div>

      {CATEGORIES.map((cat) => {
        const items = catalog.filter((c) => c.category === cat);
        if (items.length === 0) return null;
        return (
          <div key={cat}>
            <div className="text-xs uppercase tracking-wider text-white/60 font-bold mb-2">{cat}</div>
            <div className="grid grid-cols-2 gap-2" data-testid={`wh-avatar-cat-${cat}`}>
              <button onClick={() => setSlot(cat, null)}
                className={`text-left border rounded-lg p-2 text-xs ${!avatar[cat] ? 'bg-[#D3D86F]/20 border-[#D3D86F] text-white' : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'}`}
                data-testid={`wh-slot-${cat}-none`}>
                <span className="font-bold">None</span>
              </button>
              {items.map((it) => {
                const isOwned = owned.has(it.item_id);
                const isActive = avatar[cat] === it.item_id;
                return (
                  <button key={it.item_id} disabled={!isOwned}
                    onClick={() => setSlot(cat, it.item_id)}
                    className={`text-left border rounded-lg p-2 text-xs relative ${
                      !isOwned ? 'bg-white/5 border-white/10 text-white/40 cursor-not-allowed'
                        : isActive ? 'bg-[#D3D86F]/20 border-[#D3D86F] text-white'
                          : 'bg-white/5 border-white/10 text-white/80 hover:bg-white/10'
                    }`}
                    data-testid={`wh-slot-${cat}-${it.item_id}`}>
                    <div className="font-bold">{it.name}</div>
                    <div className="text-[10px] text-white/50">{it.cost} coins</div>
                    {!isOwned && <Lock className="h-3 w-3 absolute top-1.5 right-1.5" />}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Shop
// ---------------------------------------------------------------------------
const ShopView = ({ data, onPurchased }) => {
  const [busy, setBusy] = useState(null);
  const owned = useMemo(
    () => new Set((data.purchases || []).map((p) => p.item_id)),
    [data.purchases]
  );
  const balance = data.wallet?.balance || 0;
  const catalog = data.shop_catalog || [];

  const buy = async (item) => {
    setBusy(item.item_id);
    try {
      await ax.post('/api/me/economy/shop/purchase', { item_id: item.item_id });
      toast.success(`Unlocked ${item.name}!`);
      onPurchased();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Purchase failed');
    } finally {
      setBusy(null);
    }
  };

  const grouped = catalog.reduce((acc, c) => {
    (acc[c.category] = acc[c.category] || []).push(c); return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="winhouse-shop">
      <div className="bg-[#D3D86F]/10 border border-[#D3D86F]/30 rounded-xl p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Coins className="h-5 w-5 text-[#D3D86F]" />
          <div className="text-white font-bold text-sm">Balance: <span data-testid="wh-shop-balance">{balance}</span> WinCoins</div>
        </div>
        <div className="text-[10px] text-white/50 uppercase tracking-wider">Virtual currency · no real money</div>
      </div>

      {Object.entries(grouped).map(([cat, items]) => (
        <div key={cat}>
          <div className="text-xs uppercase tracking-wider text-white/60 font-bold mb-2">{cat}</div>
          <div className="grid grid-cols-2 gap-2" data-testid={`wh-shop-cat-${cat}`}>
            {items.map((it) => {
              const isOwned = owned.has(it.item_id);
              const canAfford = balance >= it.cost;
              return (
                <div key={it.item_id} className={`border rounded-xl p-3 ${isOwned ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-white/5 border-white/10'}`}
                  data-testid={`wh-shop-item-${it.item_id}`}>
                  <div className="text-white text-sm font-bold flex items-center gap-1.5">
                    {isOwned && <Check className="h-3.5 w-3.5 text-emerald-400" />}
                    {it.name}
                  </div>
                  <div className="text-[#D3D86F] text-xs font-bold mt-1">{it.cost} coins</div>
                  <button
                    disabled={isOwned || !canAfford || busy === it.item_id}
                    onClick={() => buy(it)}
                    className={`mt-2 w-full h-8 rounded-lg text-[10px] uppercase tracking-wider font-black ${
                      isOwned ? 'bg-emerald-500/20 text-emerald-300 cursor-default'
                        : canAfford ? 'bg-[#D3D86F] hover:bg-[#c4ca5e] text-black'
                          : 'bg-white/10 text-white/40 cursor-not-allowed'
                    }`}
                    data-testid={`wh-shop-buy-${it.item_id}`}>
                    {busy === it.item_id ? <Loader2 className="h-3 w-3 animate-spin inline" />
                      : isOwned ? 'Owned'
                        : canAfford ? 'Unlock' : 'Not enough'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Trophy Cabinet
// ---------------------------------------------------------------------------
const TrophyView = ({ data }) => (
  <div className="space-y-3" data-testid="winhouse-trophies">
    {(data.trophies || []).length === 0 && <Empty>No trophies yet. Unlock achievements to earn permanent trophies.</Empty>}
    <div className="grid grid-cols-2 gap-3">
      {(data.trophies || []).map((t) => (
        <div key={t.trophy_id} className="bg-gradient-to-br from-amber-500/10 to-yellow-600/10 border border-amber-500/30 rounded-xl p-3"
          data-testid={`wh-trophy-${t.trophy_id}`}>
          <Trophy className="h-6 w-6 text-amber-400 mb-1.5" />
          <div className="text-white text-sm font-bold">{t.title}</div>
          <div className="text-white/60 text-[10px] mt-0.5">{t.subtitle}</div>
          <div className="text-white/40 text-[10px] mt-1.5">{new Date(t.awarded_at).toLocaleDateString()}</div>
        </div>
      ))}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Legacy Museum
// ---------------------------------------------------------------------------
const MuseumView = ({ data }) => (
  <div className="space-y-2" data-testid="winhouse-museum">
    {(data.museum || []).length === 0 && <Empty>Your legacy timeline is empty. Milestones will appear here permanently.</Empty>}
    <div className="relative pl-6">
      <div className="absolute left-2 top-2 bottom-2 w-0.5 bg-white/10" />
      {(data.museum || []).map((m) => (
        <div key={m.entry_id} className="relative py-2" data-testid={`wh-museum-${m.entry_id}`}>
          <div className="absolute -left-4 top-3.5 w-3 h-3 rounded-full bg-[#D3D86F] border-2 border-black" />
          <div className="bg-white/5 border border-white/10 rounded-lg p-3 ml-2">
            <div className="text-white text-sm font-bold">{m.title}</div>
            <div className="text-white/60 text-[10px] mt-0.5">{m.subtitle}</div>
            <div className="text-white/40 text-[10px] mt-1">{new Date(m.at).toLocaleString()}</div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Referral Panel
// ---------------------------------------------------------------------------
const ReferralView = ({ walletBalance }) => {
  const [ref, setRef] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await ax.get('/api/me/economy/referral');
      setRef(r.data);
    } catch (_e) { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const fullLink = ref?.referral_code
    ? `${window.location.origin}/student/signup?ref=${ref.referral_code}`
    : '';

  const earnedCoins = useMemo(() => {
    if (!ref?.invitees) return 0;
    let total = 0;
    for (const i of ref.invitees) {
      if (i.stage === 'signup' || i.stage === 'booked') total += 100;
      if (i.stage === 'booked') total += 300;
    }
    return total;
  }, [ref]);

  const copyCode = () => {
    if (!ref?.referral_code) return;
    navigator.clipboard.writeText(ref.referral_code);
    toast.success('Referral code copied');
  };
  const copyLink = () => {
    if (!fullLink) return;
    navigator.clipboard.writeText(fullLink);
    toast.success('Referral link copied — share with a friend');
  };

  return (
    <div className="space-y-4" data-testid="winhouse-referral">
      <div className="bg-gradient-to-br from-[#D3D86F]/20 to-yellow-500/10 border border-[#D3D86F]/40 rounded-2xl p-5">
        <Sparkles className="h-6 w-6 text-[#D3D86F] mb-2" />
        <div className="text-white text-sm font-bold">Invite friends to Winhouse</div>
        <div className="text-white/70 text-xs mt-1">Earn 100 WinCoins for every friend who signs up · 300 more when they book a lesson.</div>

        <div className="mt-3 space-y-2">
          <div>
            <div className="text-[9px] uppercase tracking-wider text-white/50 font-bold mb-1">Your code</div>
            <div className="flex items-center gap-2 bg-black/40 border border-white/10 rounded-lg p-2">
              <code className="text-[#D3D86F] text-sm font-mono flex-1" data-testid="wh-referral-code">
                {ref?.referral_code || '—'}
              </code>
              <button onClick={copyCode}
                className="h-8 px-3 rounded bg-white/10 hover:bg-white/20 text-white text-[10px] uppercase tracking-wider font-black inline-flex items-center gap-1"
                data-testid="wh-referral-copy-code">
                <Copy className="h-3 w-3" /> Copy Code
              </button>
            </div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-white/50 font-bold mb-1">Shareable link</div>
            <div className="flex items-center gap-2 bg-black/40 border border-white/10 rounded-lg p-2">
              <code className="text-white/80 text-[10px] font-mono flex-1 truncate" data-testid="wh-referral-link">
                {fullLink || '—'}
              </code>
              <button onClick={copyLink}
                className="h-8 px-3 rounded bg-[#D3D86F] hover:bg-[#c4ca5e] text-black text-[10px] uppercase tracking-wider font-black inline-flex items-center gap-1"
                data-testid="wh-referral-copy">
                <Copy className="h-3 w-3" /> Copy Link
              </button>
            </div>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2">
          <div className="bg-black/30 border border-white/10 rounded-lg p-2 text-center">
            <div className="text-[9px] uppercase tracking-wider text-white/60 font-bold">Invitees</div>
            <div className="text-lg font-black text-white mt-0.5" data-testid="wh-referral-count">{ref?.invitees?.length || 0}</div>
          </div>
          <div className="bg-[#D3D86F]/10 border border-[#D3D86F]/30 rounded-lg p-2 text-center">
            <div className="text-[9px] uppercase tracking-wider text-[#D3D86F] font-bold">Coins from referrals</div>
            <div className="text-lg font-black text-[#D3D86F] mt-0.5" data-testid="wh-referral-earned">{earnedCoins}</div>
          </div>
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-wider text-white/60 font-bold mb-2">Your Invitees · {ref?.invitees?.length || 0}</div>
        {(ref?.invitees || []).length === 0 && <Empty>Nobody has used your code yet.</Empty>}
        <div className="space-y-1.5" data-testid="wh-referral-invitees">
          {(ref?.invitees || []).map((i) => (
            <div key={i.referral_id} className="bg-white/5 border border-white/10 rounded-lg p-2 text-xs text-white/70 flex items-center justify-between"
              data-testid={`wh-referral-invitee-${i.referral_id}`}>
              <div>
                <div className="font-bold text-white">{i.invitee_email}</div>
                <div className="text-[10px] text-white/50">{new Date(i.at).toLocaleDateString()}</div>
              </div>
              <div className={`px-2 py-0.5 rounded text-[9px] uppercase tracking-wider font-black ${
                i.stage === 'booked' ? 'bg-emerald-500/20 text-emerald-300'
                  : 'bg-blue-500/20 text-blue-300'
              }`}>
                {i.stage === 'booked' ? '+400' : '+100'} · {i.stage}
              </div>
            </div>
          ))}
        </div>
      </div>
      {typeof walletBalance === 'number' && (
        <div className="text-[10px] text-white/40 text-center">Wallet balance: {walletBalance} WinCoins</div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Root Panel
// ---------------------------------------------------------------------------
export default function WinhousePanel() {
  const [data, setData] = useState(null);
  const [houseLb, setHouseLb] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sub, setSub] = useState('overview');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [me, lb] = await Promise.all([
        ax.get('/api/me/economy'),
        ax.get('/api/houses/leaderboard'),
      ]);
      setData(me.data);
      setHouseLb(lb.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load Winhouse');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return (
      <div className="p-8 text-center" data-testid="winhouse-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#D3D86F] mx-auto" />
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-slate-900 via-black to-slate-900 border border-[#D3D86F]/30 rounded-2xl p-5 shadow-2xl"
      data-testid="winhouse-panel">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-2xl">🏰</span>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#D3D86F]">Winhouse Economy</div>
          <div className="text-white text-lg font-black">Your Identity & Rewards</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-5" data-testid="winhouse-subtabs">
        <SubTab active={sub === 'overview'}  onClick={() => setSub('overview')}  testid="wh-sub-overview"  icon={Shield}>Overview</SubTab>
        <SubTab active={sub === 'avatar'}    onClick={() => setSub('avatar')}    testid="wh-sub-avatar"    icon={Users}>Avatar</SubTab>
        <SubTab active={sub === 'shop'}      onClick={() => setSub('shop')}      testid="wh-sub-shop"      icon={ShoppingBag}>Shop</SubTab>
        <SubTab active={sub === 'trophies'}  onClick={() => setSub('trophies')}  testid="wh-sub-trophies"  icon={Trophy}>Trophies</SubTab>
        <SubTab active={sub === 'museum'}    onClick={() => setSub('museum')}    testid="wh-sub-museum"    icon={Landmark}>Legacy</SubTab>
        <SubTab active={sub === 'referral'}  onClick={() => setSub('referral')}  testid="wh-sub-referral"  icon={Sparkles}>Referrals</SubTab>
      </div>

      {sub === 'overview' && <OverviewView data={data} houseLeaderboard={houseLb} />}
      {sub === 'avatar'   && <AvatarView   data={data} onSaved={load} />}
      {sub === 'shop'     && <ShopView     data={data} onPurchased={load} />}
      {sub === 'trophies' && <TrophyView   data={data} />}
      {sub === 'museum'   && <MuseumView   data={data} />}
      {sub === 'referral' && <ReferralView walletBalance={data.wallet?.balance} />}
    </div>
  );
}
